import { useMemo } from "react";
import { useEnduserStore } from "../store/hooks";
import { useEnduserFormResponses } from "./useEnduserFormResponses";
import { useStripeSubscriptions } from "./useStripeSubscriptions";
import { useCalendarEvents } from "./useCalendarEvents";
import { useEnduserOrders } from "./useEnduserOrders";
import { useEnduserChatRooms } from "./useEnduserChatRooms";
import { useEnduserFiles } from "./useEnduserFiles";
import { useEnduserContent } from "./useEnduserContent";
import { useEnduserPurchases } from "./useEnduserPurchases";
import { useAppointmentBookingPages } from "./useAppointmentBookingPages";
import type { DataSourceType } from "@tellescope/visibility-schema";
import {
  normalizeStripeSubscription,
  type VisibilityContext,
  type VisibilityContextLoadingState,
} from "../../shared/visibility/types";

export interface UseVisibilityContextOptions {
  /** Set of data source types required for visibility evaluation */
  requiredSources: Set<DataSourceType>;
}

export interface UseVisibilityContextResult {
  /** The visibility context containing all required data, null if loading */
  context: VisibilityContext | null;
  /** Whether any required data is still loading */
  isLoading: boolean;
  /** Detailed loading state per source */
  loadingState: VisibilityContextLoadingState;
}

/**
 * Hook for building a VisibilityContext with lazy data fetching.
 *
 * Only fetches data sources that are actually needed for evaluation,
 * based on the requiredSources parameter. This enables efficient
 * evaluation without loading unnecessary data.
 *
 * @param options - Configuration including required data sources
 * @returns The visibility context, loading state, and detailed loading info
 *
 * @example
 * ```tsx
 * // Extract required sources from a condition
 * const requiredSources = extractDataSources(condition);
 *
 * // Build context (only fetches needed data)
 * const { context, isLoading } = useVisibilityContext({ requiredSources });
 *
 * // Evaluate when ready
 * if (!isLoading && context) {
 *   const isVisible = evaluateVisibility(condition, context);
 * }
 * ```
 */
export function useVisibilityContext(options: UseVisibilityContextOptions): UseVisibilityContextResult {
  const { requiredSources } = options;

  // Always get enduser from store (synchronous, always available)
  const enduser = useEnduserStore((state) => state.currentEnduser);

  // Determine which sources need to be fetched
  const needsFormResponses = requiredSources.has("formResponse");
  const needsStripeSubscriptions = requiredSources.has("stripeSubscription");
  const needsAppointments = requiredSources.has("appointment");
  const needsAppointmentBookingPages = requiredSources.has("appointmentBookingPage");
  const needsOrders = requiredSources.has("order");
  const needsChatRooms = requiredSources.has("chatRoom");
  const needsFiles = requiredSources.has("file");
  const needsContent = requiredSources.has("content");
  const needsPurchases = requiredSources.has("purchase");

  // Conditionally fetch form responses based on requirement
  const { formResponses, isLoading: formResponsesLoading } = useEnduserFormResponses({
    enabled: needsFormResponses,
  });

  // Conditionally fetch stripe subscriptions based on requirement
  const { subscriptions: stripeSubscriptions, isLoading: stripeSubscriptionsLoading } = useStripeSubscriptions({
    limit: 100, // Fetch all subscriptions for visibility evaluation
  });

  // Conditionally fetch appointments based on requirement
  const { events: appointments, isLoading: appointmentsLoading } = useCalendarEvents({
    category: "all", // Include all appointments for visibility evaluation
    enabled: needsAppointments,
  });

  // Conditionally fetch appointment booking pages based on requirement
  const { bookingPages: appointmentBookingPages, isLoading: appointmentBookingPagesLoading } =
    useAppointmentBookingPages({
      enabled: needsAppointmentBookingPages,
    });

  // Conditionally fetch orders based on requirement
  const { orders, isLoading: ordersLoading } = useEnduserOrders({
    enabled: needsOrders,
  });

  // Conditionally fetch chat rooms based on requirement
  const { chatRooms, isLoading: chatRoomsLoading } = useEnduserChatRooms({
    enabled: needsChatRooms,
  });

  // Conditionally fetch files based on requirement
  const { files, isLoading: filesLoading } = useEnduserFiles({
    enabled: needsFiles,
  });

  // Conditionally fetch content based on requirement
  const { content: contents, isLoading: contentsLoading } = useEnduserContent({
    enabled: needsContent,
  });

  // Conditionally fetch purchases based on requirement
  const { purchases, isLoading: purchasesLoading } = useEnduserPurchases({
    enabled: needsPurchases,
  });

  // Calculate overall loading state
  const isLoading =
    (needsFormResponses && formResponsesLoading) ||
    (needsStripeSubscriptions && stripeSubscriptionsLoading) ||
    (needsAppointments && appointmentsLoading) ||
    (needsAppointmentBookingPages && appointmentBookingPagesLoading) ||
    (needsOrders && ordersLoading) ||
    (needsChatRooms && chatRoomsLoading) ||
    (needsFiles && filesLoading) ||
    (needsContent && contentsLoading) ||
    (needsPurchases && purchasesLoading);

  // Normalize stripe subscriptions for visibility evaluation
  const normalizedStripeSubscriptions = useMemo(
    () => (needsStripeSubscriptions ? stripeSubscriptions.map(normalizeStripeSubscription) : undefined),
    [needsStripeSubscriptions, stripeSubscriptions]
  );

  // Build detailed loading state
  const loadingState: VisibilityContextLoadingState = useMemo(
    () => ({
      isLoading,
      sources: {
        enduser: false, // Enduser is always synchronously available from store
        formResponses: needsFormResponses && formResponsesLoading,
        stripeSubscriptions: needsStripeSubscriptions && stripeSubscriptionsLoading,
        appointments: needsAppointments && appointmentsLoading,
        appointmentBookingPages: needsAppointmentBookingPages && appointmentBookingPagesLoading,
        orders: needsOrders && ordersLoading,
        chatRooms: needsChatRooms && chatRoomsLoading,
        files: needsFiles && filesLoading,
        contents: needsContent && contentsLoading,
        purchases: needsPurchases && purchasesLoading,
      },
    }),
    [
      isLoading,
      needsFormResponses,
      formResponsesLoading,
      needsStripeSubscriptions,
      stripeSubscriptionsLoading,
      needsAppointments,
      appointmentsLoading,
      needsAppointmentBookingPages,
      appointmentBookingPagesLoading,
      needsOrders,
      ordersLoading,
      needsChatRooms,
      chatRoomsLoading,
      needsFiles,
      filesLoading,
      needsContent,
      contentsLoading,
      needsPurchases,
      purchasesLoading,
    ]
  );

  // Build the context (null while loading critical data)
  const context: VisibilityContext | null = useMemo(() => {
    // Need enduser at minimum
    if (!enduser) return null;

    // Wait for required data to load
    if (isLoading) return null;

    return {
      enduser,
      formResponses: needsFormResponses ? formResponses : undefined,
      stripeSubscriptions: normalizedStripeSubscriptions,
      // Array sources for block visibility (any-match semantics)
      appointments: needsAppointments ? appointments : undefined,
      appointmentBookingPages: needsAppointmentBookingPages ? appointmentBookingPages : undefined,
      orders: needsOrders ? orders : undefined,
      chatRooms: needsChatRooms ? chatRooms : undefined,
      files: needsFiles ? files : undefined,
      contents: needsContent ? contents : undefined,
      purchases: needsPurchases ? purchases : undefined,
    };
  }, [
    enduser,
    isLoading,
    needsFormResponses,
    formResponses,
    normalizedStripeSubscriptions,
    needsAppointments,
    appointments,
    needsAppointmentBookingPages,
    appointmentBookingPages,
    needsOrders,
    orders,
    needsChatRooms,
    chatRooms,
    needsFiles,
    files,
    needsContent,
    contents,
    needsPurchases,
    purchases,
  ]);

  return { context, isLoading, loadingState };
}
